<script setup lang="ts">
  import { computed } from 'vue';
  import { FormInput, Separator } from '@/shared/ui';
  import type { RecordConfigMatrix } from '../../model/record-config.types';

  const TITLE_FIELD_ID = 'record-config-title';
  const TITLE_MAX_LENGTH = 255;

  const props = defineProps<{
    matrix: RecordConfigMatrix;
    title: string;
    functionalInspectionTypeLabel: string | null;
  }>();

  const emit = defineEmits<{
    'update:title': [value: string];
  }>();

  const revisionLabel = computed(() => String(props.matrix.revision).padStart(2, '0'));

  const originFields = computed(() => {
    const fields = [
      { key: 'origin', label: 'Origem do Registro:', value: props.matrix.recordOrigin.name },
      { key: 'type', label: 'Tipo do Registro:', value: props.matrix.recordType.name },
    ];
    if (props.functionalInspectionTypeLabel !== null) {
      fields.push({
        key: 'inspection',
        label: 'Tipo de Inspeção Funcional:',
        value: props.functionalInspectionTypeLabel,
      });
    }
    return fields;
  });
</script>

<template>
  <div class="flex flex-col" data-testid="record-config-header">
    <div class="flex flex-wrap items-start gap-6 px-8 py-6">
      <div class="flex flex-col gap-2">
        <span class="text-sm font-semibold text-gray-07">Registro:</span>
        <div
          class="flex h-12 items-center rounded-lg border border-gray-02 bg-gray-02/20 px-5 text-sm text-gray-07"
          data-testid="record-config-code"
        >
          {{ matrix.code }}
        </div>
      </div>
      <div class="flex flex-col gap-2">
        <span class="text-sm font-semibold text-gray-07">Rev.:</span>
        <div
          class="flex h-12 items-center rounded-lg border border-gray-02 bg-gray-02/20 px-5 text-sm text-gray-07"
          data-testid="record-config-revision"
        >
          {{ revisionLabel }}
        </div>
      </div>
      <FormInput
        :id="TITLE_FIELD_ID"
        class="min-w-64 flex-1"
        :model-value="title"
        label="Título"
        type="text"
        placeholder="Digite o título do registro"
        :max-length="TITLE_MAX_LENGTH"
        label-class="text-sm text-gray-07 font-semibold"
        input-class="h-12 bg-white-01 text-sm text-gray-07"
        data-testid="record-config-title"
        @update:model-value="emit('update:title', $event)"
      />
    </div>

    <Separator />

    <div class="flex flex-wrap items-start gap-6 px-8 py-6">
      <div v-for="field in originFields" :key="field.key" class="flex flex-col gap-2">
        <span class="text-sm font-semibold text-gray-07">{{ field.label }}</span>
        <div
          class="flex h-12 items-center rounded-lg border border-gray-02 bg-gray-02/20 px-5 text-sm text-gray-07"
          :data-testid="`record-config-${field.key}`"
        >
          {{ field.value }}
        </div>
      </div>
    </div>
  </div>
</template>
